import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { IntlShape, useIntl } from 'react-intl';
import { Button } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';
import { StyledNotificationDrawer } from './style';
import NotificationTypeTag from '../../../admin/components/NotificationTypeTag';

type NotificationFieldChange = {
  name: string;
  old: string | number | null;
  new: string | number | null;
};

type NotificationPropertyChange = {
  property: string;
  old: string | number | null;
  new: string | number | null;
};

type NotificationChanges = Record<
  string,
  | NotificationFieldChange
  | string[]
  | Record<
      string,
      NotificationPropertyChange[] | { old: string | null; new: string | null }
    >
>;

export type NotificationData = {
  title?: string;
  body?: string;
  type?: string;
  action_url?: string;
  action_text?: string;
  raw?: string;
  sender_farm_id?: number;
  sender_farm_name?: string;
  message_id?: string;
  changes?: NotificationChanges;
  application_url?: string;
};

export type DatabaseNotification = {
  id: string;
  type: string;
  data: NotificationData | string;
  read_at: string | null;
  archived_at: string | null;
  created_at: string;
};

const FIELD_CHANGE_KEYS = new Set([
  'title',
  'description',
  'publication_start_date',
  'publication_end_date',
  'address',
  'delivery_radius_km',
  'transaction_type',
  'is_private',
  'single_producer_only',
  'auto_extend_publication',
  'extend_days',
]);

const LIST_KEYS = new Set([
  'products_added',
  'products_removed',
  'delivery_added',
  'delivery_removed',
]);

const UPDATED_KEYS = new Set(['products_updated', 'delivery_updated']);

const isFieldChange = (
  key: string,
  val: Record<string, string | number | null>
): val is NotificationFieldChange =>
  FIELD_CHANGE_KEYS.has(key) &&
  typeof val === 'object' &&
  val !== null &&
  'name' in val;

const formatValue = (val: string | number | null | undefined): string =>
  val === null || val === undefined ? '-' : String(val);

const renderChangesSection = (
  changes: NotificationChanges,
  intl: IntlShape
) => {
  const fieldRows: React.ReactNode[] = [];
  const listSections: React.ReactNode[] = [];
  const updatedSections: React.ReactNode[] = [];
  const contractRows: React.ReactNode[] = [];

  for (const [key, value] of Object.entries(changes)) {
    if (LIST_KEYS.has(key) && Array.isArray(value) && value.length > 0) {
      const labelId = key.includes('added')
        ? 'notification.changes_added'
        : 'notification.changes_removed';
      listSections.push(
        <div key={key}>
          <div className="dn-changes-section-title">
            {intl.formatMessage({ id: labelId })}
          </div>
          <ul className="dn-change-list">
            {value.map(
              (
                item: string | Record<string, string | number | null>,
                i: number
              ) => {
                if (typeof item === 'string') return <li key={i}>{item}</li>;
                const mainParts = [
                  item.name,
                  item.required_quantity,
                  item.storage_unit,
                ].filter(Boolean);
                const extras = [
                  item.min_acceptable_offer_quantity
                    ? `(min: ${item.min_acceptable_offer_quantity})`
                    : null,
                  item.quality_marks || null,
                ].filter(Boolean);
                const label = [mainParts.join(' '), ...extras].join(' — ');
                return <li key={i}>{label}</li>;
              }
            )}
          </ul>
        </div>
      );
    } else if (
      UPDATED_KEYS.has(key) &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      for (const [entityName, props] of Object.entries(
        value as Record<string, NotificationPropertyChange[]>
      )) {
        if (!Array.isArray(props)) continue;
        updatedSections.push(
          <div key={`${key}-${entityName}`}>
            <div className="dn-changes-section-title">{entityName}</div>
            {props.map((prop: NotificationPropertyChange, i: number) => (
              <div className="dn-change-row" key={i}>
                <span className="dn-change-label">{prop.property}</span>
                <div className="dn-change-values">
                  <span className="dn-change-old">{formatValue(prop.old)}</span>
                  <span className="dn-change-arrow">&rarr;</span>
                  <span className="dn-change-new">{formatValue(prop.new)}</span>
                </div>
              </div>
            ))}
          </div>
        );
      }
    } else if (
      key === 'contract_terms' &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      for (const [label, change] of Object.entries(
        value as Record<string, { old: string | null; new: string | null }>
      )) {
        contractRows.push(
          <div className="dn-change-row" key={`ct-${label}`}>
            <span className="dn-change-label">{label}</span>
            <div className="dn-change-values">
              <span className="dn-change-old">{formatValue(change.old)}</span>
              <span className="dn-change-arrow">&rarr;</span>
              <span className="dn-change-new">{formatValue(change.new)}</span>
            </div>
          </div>
        );
      }
    } else if (
      isFieldChange(key, value as Record<string, string | number | null>)
    ) {
      const fc = value as NotificationFieldChange;
      fieldRows.push(
        <div className="dn-change-row" key={key}>
          <span className="dn-change-label">{fc.name}</span>
          <div className="dn-change-values">
            <span className="dn-change-old">{formatValue(fc.old)}</span>
            <span className="dn-change-arrow">&rarr;</span>
            <span className="dn-change-new">{formatValue(fc.new)}</span>
          </div>
        </div>
      );
    }
  }

  const hasContent =
    fieldRows.length > 0 ||
    listSections.length > 0 ||
    updatedSections.length > 0 ||
    contractRows.length > 0;
  if (!hasContent) return null;

  return (
    <div className="dn-changes">
      <div className="dn-changes-title">
        {intl.formatMessage({ id: 'notification.changes_title' })}
      </div>
      {fieldRows}
      {listSections}
      {updatedSections}
      {contractRows}
    </div>
  );
};

interface Props {
  open: boolean;
  notification: DatabaseNotification | null;
  onClose: () => void;
  onMarkedRead: () => void;
  onMarkedUnread: () => void;
  onArchived: () => void;
  onUnarchived: () => void;
}

function DrawerNotification({
  open,
  notification,
  onClose,
  onMarkedRead,
  onMarkedUnread,
  onArchived,
  onUnarchived,
}: Props): JSX.Element | null {
  const intl = useIntl();

  const { appendData: markOneRead, isLoading } = useQueryApiClient({
    request: { url: '/api/v2/notifications/:id/read', method: 'POST' },
    onSuccess: () => {
      onMarkedRead?.();
      message.success(intl.formatMessage({ id: 'notification.read_success' }));
    },
  });

  const { appendData: markBulkUnread, isLoading: isUnreadLoading } =
    useQueryApiClient({
      request: { url: '/api/v2/notifications/bulk-unread', method: 'POST' },
      onSuccess: () => {
        onMarkedUnread?.();
        message.success(
          intl.formatMessage({ id: 'notification.unread_success' })
        );
      },
    });

  const { appendData: archiveOne, isLoading: isArchiveLoading } =
    useQueryApiClient({
      request: { url: '/api/v2/notifications/:id/archive', method: 'POST' },
      onSuccess: () => {
        onArchived?.();
        message.success(
          intl.formatMessage({ id: 'notification.archived_success' })
        );
        onClose?.();
      },
    });

  const { appendData: unarchiveOne, isLoading: isUnarchiveLoading } =
    useQueryApiClient({
      request: { url: '/api/v2/notifications/:id/unarchive', method: 'POST' },
      onSuccess: () => {
        onUnarchived?.();
        message.success(
          intl.formatMessage({ id: 'notification.unarchived_success' })
        );
      },
    });

  const info = useMemo(() => {
    if (!notification) return null;

    let payload: NotificationData = notification.data as NotificationData;
    if (typeof notification.data === 'string') {
      try {
        payload = JSON.parse(notification.data) as NotificationData;
      } catch {
        payload = { raw: notification.data };
      }
    }

    return {
      id: notification.id,
      createdAtText: notification.created_at
        ? dayjs(notification.created_at).format('DD.MM.YYYY | HH:mm')
        : '-',
      isRead: Boolean(notification.read_at),
      isArchived: Boolean(notification.archived_at),
      title: payload?.title || '',
      body: payload?.body || '',
      shortType: payload?.type,
      fallbackType: notification.type,
      actionUrl: payload?.action_url,
      actionText: payload?.action_text,
      applicationUrl: payload?.application_url,
      changes: payload?.changes,
      payload,
    };
  }, [notification]);

  if (!notification || !info) return null;

  const handleMarkRead = () => {
    if (notification.read_at) {
      onMarkedRead?.();
      return;
    }
    markOneRead(null, { id: notification.id });
  };

  const handleMarkUnread = () => {
    markBulkUnread({ ids: [notification.id] });
  };

  const handleArchive = () => {
    archiveOne(null, { id: notification.id });
  };

  const handleUnarchive = () => {
    unarchiveOne(null, { id: notification.id });
  };

  const handleOpenAction = () => {
    if (!info.actionUrl) return;

    const isMessageType = info.shortType === 'messages.sent';

    if (isMessageType) {
      window.location.href = info.actionUrl;
    } else {
      window.open(info.actionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const drawerTitle = (
    <div className="dn-header">
      <div className="dn-title">{info.title}</div>
      <div className="dn-meta">
        <span className="dn-time">{info.createdAtText}</span>
        <NotificationTypeTag
          shortType={info.shortType}
          fallbackType={info.fallbackType}
          size="md"
        />
      </div>
    </div>
  );

  const drawerFooter = (
    <div className="drawer-footer">
      {info.isRead ? (
        <Button
          type="primary"
          label={intl.formatMessage({
            id: 'notification.mark_as_unread',
          })}
          onClick={handleMarkUnread}
          loading={isUnreadLoading}
          disabled={info.isArchived}
        />
      ) : (
        <Button
          type="primary"
          label={intl.formatMessage({
            id: 'notification.mark_as_read',
          })}
          onClick={handleMarkRead}
          loading={isLoading}
          disabled={info.isArchived}
        />
      )}
      {info.isArchived ? (
        <Button
          type="primary"
          label={intl.formatMessage({
            id: 'notification.unarchive',
          })}
          onClick={handleUnarchive}
          loading={isUnarchiveLoading}
        />
      ) : (
        <Button
          type="primary"
          label={intl.formatMessage({
            id: 'notification.archive',
          })}
          onClick={handleArchive}
          loading={isArchiveLoading}
        />
      )}
      <Button
        label={intl.formatMessage({
          id: 'general.close',
        })}
        onClick={onClose}
      />
    </div>
  );

  return (
    <StyledNotificationDrawer
      open={open}
      onClose={onClose}
      title={drawerTitle}
      width={923}
      destroyOnClose
      footer={drawerFooter}
      zIndex={1100}
    >
      <div className="dn-body">
        {info.body ? <p className="dn-text">{info.body}</p> : null}

        {info.changes ? renderChangesSection(info.changes, intl) : null}

        {info.actionUrl && info.actionText ? (
          <Button
            type="primary"
            label={info.actionText}
            onClick={handleOpenAction}
          />
        ) : null}
        {info.applicationUrl ? (
          <Button
            label={intl.formatMessage({ id: 'notification.view_applications' })}
            onClick={() =>
              window.open(info.applicationUrl, '_blank', 'noopener,noreferrer')
            }
          />
        ) : null}
      </div>
    </StyledNotificationDrawer>
  );
}

export default DrawerNotification;
